import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, useAttrs, onMounted, nextTick } from 'vue'
import { detectCountryMask, detectCountryMaskOrNull, INTERNATIONAL_MAX_DIGITS, PHONE_MASKS } from '@/utils/phoneMasks'

interface Props {
  type: string,
  phoneMaskLength?: number, // Получаем длину маски для ограничения
  modelValue?: string, // Значение поля для отслеживания
}

const p = 'password'
const t = 'text'


export default /*#__PURE__*/_defineComponent({
  __name: 'SimpleInput',
  props: {
    type: { default: 'text' },
    phoneMaskLength: { default: undefined },
    modelValue: { default: undefined }
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const props = __props

const $attrs = useAttrs()


const inputType = ref<string>(props.type)
const inputRef = ref<HTMLInputElement>()

const isPasswordField = computed(() => props.type === p)
const passwordIsVisible = computed(() => inputType.value === t)

// Логика maska больше не нужна - используем собственное форматирование

const toggleType = () => {
  inputType.value = passwordIsVisible.value ? p : t
}

// Флаг для предотвращения циклических вызовов
let isFormatting = false

// Автоформатирование при загрузке страницы
onMounted(() => {
  // Проверяем телефонные поля через nextTick (после VeeField)
  if (props.type === 'tel') {
    nextTick(() => {
      // Применяем только для полей phone
      if (!inputRef.value?.name || !inputRef.value.name.includes('phone')) {
        return
      }
      
      if (isFormatting) return // Защита от циклов
      
      const currentValue = inputRef.value?.value
      if (currentValue && currentValue.replace(/\D/g, '').length > 0) {
        const formattedPhone = formatPhoneFromDB(currentValue)
        
        if (formattedPhone && formattedPhone !== currentValue) {
          isFormatting = true
          inputRef.value.value = formattedPhone
          
          // Эмитируем событие для Vue.js
          const inputEvent = new Event('input', { bubbles: true })
          inputRef.value.dispatchEvent(inputEvent)
          
          // Сбрасываем флаг через микротаск
          setTimeout(() => { isFormatting = false }, 0)
        }
      }
    })
  }
})

// Резервный watcher на modelValue (в случае если onMounted не сработал)
watch(() => props.modelValue, (newValue) => {
  if (isFormatting) return // Защита от циклов
  
  if (props.type === 'tel' && newValue && inputRef.value) {
    // Применяем только для полей phone  
    if (!inputRef.value.name || !inputRef.value.name.includes('phone')) {
      return
    }
    
    const currentFieldValue = inputRef.value.value
    
    if (newValue !== currentFieldValue && newValue.replace(/\D/g, '').length > 0) {
      const formattedPhone = formatPhoneFromDB(newValue)
      
      if (formattedPhone && formattedPhone !== newValue) {
        isFormatting = true
        inputRef.value.value = formattedPhone
        
        const inputEvent = new Event('input', { bubbles: true })
        inputRef.value.dispatchEvent(inputEvent)
        
        // Сбрасываем флаг через микротаск
        setTimeout(() => { isFormatting = false }, 0)
      }
    }
  }
}, { immediate: true })

// Программное форматирование по маске
const formatByMask = (value: string, mask: string): string => {
  if (!value || !mask) return value
  
  // Извлекаем все цифры из входного значения
  const allDigits = value.replace(/\D/g, '')
  if (!allDigits) return ''
  
  // Извлекаем код страны из маски (все что до первого #)
  const maskBeforeHash = mask.substring(0, mask.indexOf('#'))
  const countryCodeDigits = maskBeforeHash.replace(/\D/g, '')
  
  // Проверяем, что введенные цифры начинаются с кода страны
  if (!allDigits.startsWith(countryCodeDigits)) {
    return value
  }
  
  // Получаем цифры номера (без кода страны)
  const phoneDigits = allDigits.substring(countryCodeDigits.length)
  
  // Начинаем форматирование с фиксированной части маски
  let formatted = maskBeforeHash
  let phoneDigitIndex = 0
  
  // Продолжаем с позиции первого #
  for (let i = mask.indexOf('#'); i < mask.length && phoneDigitIndex < phoneDigits.length; i++) {
    const maskChar = mask[i]
    if (maskChar === '#') {
      formatted += phoneDigits[phoneDigitIndex]
      phoneDigitIndex++
    } else {
      formatted += maskChar
    }
  }
  
  return formatted
}

// Получение текущей маски для номера
const getCurrentMask = (phoneValue: string): string => {
  const maskConfig = detectCountryMask(phoneValue)
  return maskConfig.mask
}

// Форматирование номера из базы данных (без + в начале)
const formatPhoneFromDB = (rawPhone: string): string => {
  if (!rawPhone) return ''
  
  // Удаляем все нецифровые символы
  const digits = rawPhone.replace(/\D/g, '')
  if (!digits) return ''
  
  // Если уже есть +, используем как есть
  let phoneWithCountryCode = digits.startsWith('+') ? digits : '+' + digits
  
  // Проверяем валидность текущего кода страны
  if (isValidCountryCode(phoneWithCountryCode)) {
    // Код валидный, применяем форматирование
    const mask = getCurrentMask(phoneWithCountryCode)
    return formatByMask(phoneWithCountryCode, mask)
  }
  
  // Если код невалидный, пробуем найти подходящий код страны
  // Проверяем все возможные коды стран из PHONE_MASKS
  for (const countryCode of Object.keys(PHONE_MASKS)) {
    const codeDigits = countryCode.replace('+', '')
    
    // Проверяем, начинается ли номер с этого кода
    if (digits.startsWith(codeDigits)) {
      phoneWithCountryCode = '+' + digits
      if (isValidCountryCode(phoneWithCountryCode)) {
        const mask = getCurrentMask(phoneWithCountryCode)
        return formatByMask(phoneWithCountryCode, mask)
      }
    }
  }
  
  // Если ничего не подошло, пробуем +38 как код по умолчанию
  const fallbackPhone = '+38' + digits
  if (isValidCountryCode(fallbackPhone)) {
    const mask = getCurrentMask(fallbackPhone)
    return formatByMask(fallbackPhone, mask)
  }
  
  // Если и это не помогло, возвращаем исходное значение
  return rawPhone
}

// Валидация кода страны
const isValidCountryCode = (value: string): boolean => {
  if (!value.startsWith('+')) return false
  
  const digits = value.replace(/\D/g, '')
  
  // Проверяем все возможные коды из PHONE_MASKS
  for (const code of Object.keys(PHONE_MASKS)) {
    const codeDigits = code.replace('+', '')
    if (digits.startsWith(codeDigits)) {
      return true
    }
  }
  
  return false
}

// Обработчики для телефонных полей с программным форматированием
// Включают валидацию кодов стран и автоматическое форматирование

const handleTelKeydown = (event: KeyboardEvent) => {
  const key = event.key
  const target = event.target as HTMLInputElement
  
  // Применяем телефонную логику только для полей phone
  if (!target.name || !target.name.includes('phone')) {
    return
  }
  
  const currentValue = target.value
  const cursorPosition = target.selectionStart || 0
  
  // Разрешаем все навигационные клавиши и функциональные
  if ([
    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Home', 'End', 'PageUp', 'PageDown'
  ].includes(key)) {
    return
  }
  
  // Разрешаем комбинации с Ctrl/Cmd (копировать, вставить и т.д.)
  if (event.ctrlKey || event.metaKey) {
    return
  }
  

  if (/\d/.test(key)) {
    const knownCountry = detectCountryMaskOrNull(currentValue)
    const maxDigits = knownCountry ? knownCountry.length : INTERNATIONAL_MAX_DIGITS
    const currentDigits = currentValue.replace(/\D/g, '').length
    if (currentDigits >= maxDigits) {
      event.preventDefault()
      return
    }
  }
  
  // Специальная логика для знака +
  if (key === '+') {
    // + разрешен только в начале и если его еще нет
    if (cursorPosition === 0 && !currentValue.includes('+')) {
      return
    } else {
      event.preventDefault()
      return
    }
  }
  
  if (/\d/.test(key)) {
    return
  }

  // Блокируем все остальные символы
  event.preventDefault()
}

// Функция для проверки, остался ли только префикс маски
const isOnlyMaskPrefix = (value: string): boolean => {
  if (!value || !value.startsWith('+')) return false
  
  try {
    // Получаем маску для текущего значения
    const mask = getCurrentMask(value)
    
    // Извлекаем статическую часть маски (до первой #)
    const firstHashIndex = mask.indexOf('#')
    if (firstHashIndex === -1) return false
    
    const prefixPart = mask.substring(0, firstHashIndex)
    
    // Проверяем, равно ли текущее значение префиксу (точно или приблизительно)
    return value === prefixPart || value === prefixPart.trim()
  } catch (e) {
    return false
  }
}

const handleTelInput = (event: Event) => {
  if (isFormatting) return // Защита от циклов
  
  const target = event.target as HTMLInputElement
  
  // Применяем телефонную логику только для полей phone
  if (!target.name || !target.name.includes('phone')) {
    return
  }
  
  const value = target.value
  
  // Получаем предыдущее значение для сравнения
  const previousValue = target.dataset.previousValue || ''
  
  // Определяем, удаляет ли пользователь (поле стало короче)
  const isDeleting = value.length < previousValue.length
  
  // Проверяем префикс маски ТОЛЬКО при удалении
  if (isDeleting && isOnlyMaskPrefix(value)) {
    // Очищаем поле полностью
    isFormatting = true
    target.value = ''
    
    const inputEvent = new Event('input', { bubbles: true })
    target.dispatchEvent(inputEvent)
    
    setTimeout(() => { isFormatting = false }, 0)
    target.dataset.previousValue = ''
    return
  }
  
  const cursorPosition = target.selectionStart || 0
  
  // Очищаем все нецифровые символы кроме +
  let cleanedValue = value.replace(/[^\d+]/g, '')
  
  // Если нет +, добавляем +38 по умолчанию
  if (!cleanedValue.startsWith('+') && cleanedValue.length > 0) {
    cleanedValue = '+38' + cleanedValue
  }
  
  // Проверяем валидность кода страны
  if (cleanedValue.startsWith('+') && !isValidCountryCode(cleanedValue)) {
    return
  }
  
  // Ограничиваем количество цифр согласно маске
  if (props.phoneMaskLength) {
    const digits = cleanedValue.replace(/\D/g, '')
    if (digits.length > props.phoneMaskLength) {
      // Обрезаем лишние цифры
      const truncatedDigits = digits.slice(0, props.phoneMaskLength)
      // Восстанавливаем код страны
      const currentMask = getCurrentMask(cleanedValue)
      const codeMatch = currentMask.match(/\+\d+/)
      if (codeMatch) {
        const countryCode = codeMatch[0]
        const countryDigits = countryCode.replace('+', '')
        cleanedValue = '+' + countryDigits + truncatedDigits.slice(countryDigits.length)
      }
    }
  }
  
  // Получаем текущую маску и применяем форматирование
  const currentMask = getCurrentMask(cleanedValue)
  const formattedValue = formatByMask(cleanedValue, currentMask)
  
  // Обновляем значение поля только если оно изменилось
  if (formattedValue !== value) {
    isFormatting = true
    target.value = formattedValue
    
    // ВАЖНО: Эмитируем событие input для Vue.js
    const inputEvent = new Event('input', { bubbles: true })
    target.dispatchEvent(inputEvent)
    
    // Корректируем позицию курсора
    const newCursorPosition = Math.min(cursorPosition + (formattedValue.length - value.length), formattedValue.length)
    
    // Устанавливаем курсор асинхронно
    setTimeout(() => {
      target.setSelectionRange(newCursorPosition, newCursorPosition)
      isFormatting = false // Сбрасываем флаг после обновления курсора
    }, 0)
    
    // Сохраняем отформатированное значение для следующего сравнения
    target.dataset.previousValue = formattedValue
  } else {
    // Даже если значение не изменилось, сохраняем текущее для отслеживания
    target.dataset.previousValue = value
  }
}

const __returned__ = { props, $attrs, p, t, inputType, inputRef, isPasswordField, passwordIsVisible, toggleType, get isFormatting() { return isFormatting }, set isFormatting(v) { isFormatting = v }, formatByMask, getCurrentMask, formatPhoneFromDB, isValidCountryCode, handleTelKeydown, isOnlyMaskPrefix, handleTelInput, ref, computed, watch, useAttrs, onMounted, nextTick, get detectCountryMask() { return detectCountryMask }, get detectCountryMaskOrNull() { return detectCountryMaskOrNull }, get INTERNATIONAL_MAX_DIGITS() { return INTERNATIONAL_MAX_DIGITS }, get PHONE_MASKS() { return PHONE_MASKS } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})